import { forwardRef, memo, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { CountrySelector } from '@components/country-selector';
import { Input } from '@components/input/src';
import { COUNTRIES } from '@constants/countries';
import classNames from 'classnames';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

import { InputPhoneProps } from './input-phone.type';

export const InputPhone = memo(
  forwardRef(
    (
      {
        name,
        value,
        placeholder,
        selectFontLight,
        initialCountry = 'adsfasfasdf',
        onChange,
        disabled,
        hasError,
        showDial,
        ...rest
      }: InputPhoneProps,
      ref: Ref<HTMLInputElement>
    ) => {
      const initialPhoneNumberCountry = value && parsePhoneNumberFromString(value)?.country;
      const initialCountryObject = initialPhoneNumberCountry
        ? COUNTRIES[initialPhoneNumberCountry]
        : COUNTRIES[initialCountry.toUpperCase()];
      const [country, setCountry] = useState(initialCountryObject);
      const [phoneValue, setPhoneValue] = useState(value);
      const asYouTypeRef = useRef(new AsYouType());
      const asYouType = asYouTypeRef.current;

      const handleCountryChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
          const selection: string = e.target.value;
          const countrySelected = COUNTRIES[selection];
          setCountry(countrySelected);
          setPhoneValue(countrySelected.dialCode);
        },
        [setCountry, setPhoneValue]
      );

      const normalizePhoneValue = useCallback(
        (rawPhoneValue: string, e: React.ChangeEvent<HTMLInputElement>) => {
          asYouType.reset();
          let formattedPhone = asYouType.input(rawPhoneValue) as string;
          // Check if dial code is missing and prepend if necessary
          if (!formattedPhone.startsWith(country.dialCode)) {
            formattedPhone = country.dialCode + formattedPhone;
          }

          setPhoneValue(formattedPhone);
          onChange(e);
        },
        [asYouType, setPhoneValue, onChange, country.dialCode]
      );

      const handlePhoneChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          const phone = e.target.value.toString();

          if (!phone.startsWith(country.dialCode)) {
            const correctedValue = country.dialCode + phone.replace(/[^\d]+/g, '');
            e.target.value = correctedValue;
          }
          normalizePhoneValue(phone, e);
        },
        [country, normalizePhoneValue]
      );

      useEffect(() => {
        if (initialCountry) {
          if (value) {
            const parsedPhone = parsePhoneNumberFromString(value);
            if (!!parsedPhone && parsedPhone.isValid()) {
              if (country.id !== parsedPhone.country && parsedPhone.country) {
                setCountry(COUNTRIES[parsedPhone.country]);
              }
              asYouType.reset();
              setPhoneValue(asYouType.input(value));
              return;
            }
          }
          setPhoneValue(country.dialCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return (
        <div className='flex gap-2'>
          <div
            className={classNames({
              'w-36': showDial,
              'w-20': !showDial,
            })}>
            <CountrySelector
              initialCountry={country.id}
              onChange={handleCountryChange}
              disabled={disabled}
              showDial={showDial}
              selectFontLight={selectFontLight}
            />
          </div>
          <div className='grow'>
            <Input
              {...rest}
              ref={ref}
              type='tel'
              onChange={handlePhoneChange}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  if (!!phoneValue && phoneValue.length <= country.dialCode.length) {
                    setPhoneValue('');
                    onChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
                  }
                }
              }}
              placeholder={placeholder}
              name={name}
              hasError={hasError}
              value={phoneValue}
              disabled={disabled}
            />
          </div>
        </div>
      );
    }
  )
);
